import React, { useState } from "react";
import { Sidebar } from "../components/ui/sidebar";
import GoalsHeader from "../components/ui/goals/goals-header";
import GoalProgressChart from "../components/ui/goals/goal-progress-chart";
import GoalsStats from "../components/ui/goals/goals-stats";
import ActiveGoals from "../components/ui/goals/active-goals";
import { ProgressStats } from "../components/ui/goals/progress-stats";
import { WeeklyGoalsSection } from "../components/ui/goals/weekly-goals-section";
import { CareerSpotlight } from "../components/ui/goals/career-spotlight";
import { useDemoData } from "../hooks/useDemoData";
import { UniversalSearch } from "../components/ui/universal-search";
import {AIChatBox} from "../components/ui/ai-chat-box";
import ProtectedRoute from "../components/ProtectedRoute";

export default function GoalsPage() {
  const { goals: demoGoals, updateGoalProgress, addGoal } = useDemoData();

  // Initialize goals based on demo data
  const [goals, setGoals] = useState(() =>
    demoGoals.map((demoGoal) => {
      const iconMap = {
        academic: "Calculator",
        career: "Gamepad2",
        personal: "BookOpen",
        skill: "Rocket",
      };

      const colorMap = {
        academic: { bg: "bg-blue-100", text: "text-blue-500" },
        career: { bg: "bg-orange-100", text: "text-orange-500" },
        personal: { bg: "bg-emerald-100", text: "text-emerald-500" },
        skill: { bg: "bg-purple-100", text: "text-purple-500" },
      };

      return {
        id: demoGoal.id,
        title: demoGoal.title,
        description: demoGoal.description,
        progress: demoGoal.progress,
        dueDate: demoGoal.targetDate
          ? demoGoal.targetDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "",
        icon: iconMap[demoGoal.category] || "BookOpen",
        iconBgColor: colorMap[demoGoal.category]?.bg || "bg-gray-100",
        iconColor: colorMap[demoGoal.category]?.text || "text-gray-500",
        isCompleted: demoGoal.progress === 100,
        completedDate: demoGoal.progress === 100 ? "Completed" : undefined,
      };
    })
  );

  // ✅ Add new goal
  const handleAddGoal = (newGoal) => {
    const categoryMap = {
      Gamepad2: "career",
      Calculator: "academic",
      BookOpen: "personal",
      Beaker: "skill",
      Music: "personal",
      Rocket: "skill",
    };

    const demoGoalData = {
      userId: "demo-user",
      title: newGoal.title,
      description: newGoal.description,
      category: categoryMap[newGoal.icon] || "personal",
      targetDate: newGoal.dueDate ? new Date(newGoal.dueDate) : undefined,
      progress: newGoal.progress,
      icon: newGoal.icon,
      color: newGoal.iconColor,
    };

    addGoal(demoGoalData);

    // ✅ Update local state
    const goal = {
      ...newGoal,
      id: Date.now().toString(),
    };
    setGoals((prev) => [...prev, goal]);
  };

  // ✅ Delete goal
  const handleDeleteGoal = (id) => {
    setGoals((prev) => prev.filter((goal) => goal.id !== id));
  };

  // ✅ Edit goal and sync demo data
  const handleEditGoal = (id, updatedGoal) => {
    setGoals((prev) =>
      prev.map((goal) => (goal.id === id ? { ...goal, ...updatedGoal } : goal))
    );

    if (updatedGoal.progress !== undefined) {
      updateGoalProgress(id, updatedGoal.progress);
    }
  };

  const handleSearch = (query) => {
    console.log("Universal search for:", query);
  };

  // ✅ Progress stats
  const completedCount = goals.filter((g) => g.isCompleted).length;
  const inProgressCount = goals.filter((g) => !g.isCompleted).length;
  const overallProgress =
    goals.length > 0 ? Math.round((completedCount / goals.length) * 100) : 0;

  return (
    <ProtectedRoute>
      <div className="flex min-h-screen bg-slate-200">
      {/* Sidebar (self-contained and fixed on desktop) */}
      <Sidebar />

      <AIChatBox />

      {/* Universal Search Component */}
      <UniversalSearch onSearch={handleSearch} />

      {/* Main content area */}
      <div className="flex-1 flex flex-col w-full transition-all duration-300 ease-in-out lg:ml-64">
        {/* ✅ Mobile header (hamburger menu display only) */}
        <div className="md:hidden flex items-center gap-3 p-4 border-b border-slate-200 bg-white sticky top-0 z-30">
          <button
            className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6 text-slate-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-slate-900">
              Goals & Progress
            </h1>
            <p className="text-sm text-slate-600">
              Track your learning journey
            </p>
          </div>
        </div>

        {/* ✅ Main content */}
        <main className="flex-1 p-4 sm:p-6 md:p-8 overflow-y-auto">
          <div className="max-w-6xl mx-auto">
            {/* Desktop header */}
            <div className="hidden md:block">
              <GoalsHeader onAddGoal={handleAddGoal} />
            </div>

            {/* Mobile add-goal button */}
            <div className="md:hidden mb-6">
              <button
                onClick={() =>
                  handleAddGoal({
                    title: "New Goal",
                    description: "Describe your goal here",
                    progress: 0,
                    dueDate: "",
                    icon: "BookOpen",
                    iconBgColor: "bg-blue-100",
                    iconColor: "text-blue-500",
                    isCompleted: false,
                  })
                }
                className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors min-h-[44px] flex items-center justify-center"
              >
                Add New Goal
              </button>
            </div>

            {/* Progress Stats from Weekly Progress */}
            <div className="mb-6 sm:mb-8 overflow-x-auto">
              <ProgressStats />
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 lg:gap-10 mb-6">
              {/* Left Column - Goals Overview */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6 md:space-y-8">
                {/* Progress section */}
                <div className="rounded-xl sm:rounded-2xl bg-slate-100 shadow-sm border border-slate-200 p-4 sm:p-6 md:p-8">
                  <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                    <GoalProgressChart progress={overallProgress} />
                    <GoalsStats
                      completed={completedCount}
                      inProgress={inProgressCount}
                      total={goals.length}
                    />
                  </div>
                </div>

                {/* Weekly Goals Section */}
                <WeeklyGoalsSection />
              </div>

              {/* Right Column - Career Spotlight */}
              <aside className="lg:col-span-1">
                <CareerSpotlight />
              </aside>
            </div>

            {/* Active goals list */}
            <ActiveGoals
              goals={goals}
              onDeleteGoal={handleDeleteGoal}
              onEditGoal={handleEditGoal}
            />
          </div>
        </main>
      </div>
    </div>
    </ProtectedRoute>
  );
}